import { ChoiceItem, QuestionRadiogroupModel, SurveyModel } from "survey-core";

export class RepairAllowedModel extends QuestionRadiogroupModel {
  private lastPayload?: string;
  private isQuestionReady = true;

  constructor(name: string) {
    super(name);

    this.choices = [new ChoiceItem(true, "Ja"), new ChoiceItem(false, "Nee")];
  }

  override getIsQuestionReady(): boolean {
    return this.isQuestionReady;
  }

  setIsQuestionReady(ready: boolean) {
    this.isQuestionReady = ready;
    this.updateIsReady();
  }

  getType() {
    return "repairAllowed";
  }

  get evaluationGroupId(): string {
    return this.getPropertyValue("evaluationGroupId");
  }

  set evaluationGroupId(val: string) {
    this.setPropertyValue("evaluationGroupId", val);
  }

  get optionalDefaultValue(): boolean | undefined {
    return this.getPropertyValue("optionalDefaultValue");
  }

  set optionalDefaultValue(val: boolean | undefined) {
    this.setPropertyValue("optionalDefaultValue", val);
  }

  onSurveyLoad(): void {
    super.onSurveyLoad();
    super.onVisibleChanged();

    const survey = this.survey as SurveyModel;
    if (!survey) return;

    survey.onCurrentPageChanged.add(() => {
      if (this.page === survey.currentPage && this.isVisible) {
        this.triggerApi(survey);
      }
    });
  }

  private triggerApi(survey: SurveyModel) {
    if (survey == null) return;
    const licensePlate = survey.getValue("licensePlate");

    const payload = {
      country: licensePlate?.country,
      identifier: licensePlate?.identifier,
      causeOfDamage: survey.getVariable("causeOfDamage"),
      otherParty: survey.getValue("otherPartyInvolved"),
      guaranteeFund: survey.getValue("damageCausedByMotorVehicle"),
      damageFromDirection: survey.getValue("damageFromDirection") ?? null,
      typeOfDamage: survey.getValue("typeOfDamage") ?? null,
    };

    const payloadKey = JSON.stringify(payload);

    // Prevent duplicate calls
    if (this.lastPayload === payloadKey) return;
    this.lastPayload = payloadKey;

    this.loadChoicesFromApi(payload, survey);
  }

  async loadChoicesFromApi(payload: object, survey: SurveyModel) {
    this.value = undefined;
    this.readOnly = true;
    this.setIsQuestionReady(false);

    try {
      const {
        processAllowed,
        viewConfiguration,
        servicePartnerCollection,
        servicePartner,
        damageClaimable,
        matchedRules,
      } = await survey
        .api!.post(
          `/driver/evaluation-engine/${this.evaluationGroupId}`,
          payload,
        )
        .then(({ data: { outputs } }) => ({
          processAllowed: outputs.processAllowed as
            | "not_allowed"
            | "required"
            | "optional",
          viewConfiguration: outputs.viewConfiguration as null | {
            components: { key: string; type: string; value: string }[];
          },
          servicePartnerCollection: outputs.service_partner_collection as
            | string
            | null,
          servicePartner: outputs.servicePartner as string | null,
          damageClaimable: outputs.damageClaimable as boolean | null,
          matchedRules: outputs.matched_rules as string[],
        }));

      survey.setValue("servicePartnerCollection", servicePartnerCollection);
      survey.setValue("servicePartner", servicePartner);
      survey.setValue("damageClaimable", damageClaimable);
      survey.setValue("matchedRules", matchedRules);

      if (viewConfiguration && viewConfiguration?.components) {
        viewConfiguration?.components?.forEach((component) => {
          if (component.key === "thankYouBody") {
            survey.thankYouBody = component.value;
          } else {
            survey.setValue(component.key, component);
          }
        });
      }

      this.value = {
        not_allowed: false,
        required: true,
        optional: this.optionalDefaultValue,
      }[processAllowed];
      this.readOnly = processAllowed !== "optional";
    } catch (err) {
      console.error("RepairAllowed API failed", err);
      this.readOnly = false;
    } finally {
      this.setIsQuestionReady(true);
    }
  }
}
